import {
  pipe,
  values,
  forEach,
  sortBy,
  prop,
  reduce
} from 'ramda';
import PCNode from '../Nodes/PCNode';
import PCPipe from '../Pipes/PCPipe';
import PCSpool from '../Spools/PCSpool';
import PCStope from '../Stopes/PCStope';
import { buildGraphIndexes, dijkstraShortestPath } from '../Route/utils';
import pairKey from '../Spools/pairKey';

export interface ExcelNodeRow {
  NODE_ID: string;
  X: number;
  Y: number;
  Z: number;
  PREVIOUS_NODE: string;
  PIPE_NAME: string;
  IS_STOPE: boolean;
  IS_BACKFILL_PLANT: boolean;
  IS_PIT: boolean;
  IS_FRICTION_LOOP: boolean;
  FRICTION_LOOP_LENGTH: number;
  PIT_NAME?: string;
  RUPTURE_DISK_PRESSURE?: number;
  CHOKE_HEAD?: number;
  STOPE_PIPE_TYPE?: string;
  STOPE_PIPE_HORIZONTAL?: number;
  STOPE_PIPE_VERTICAL?: number;
}

export interface StopeRouteOverride {
  NODE_ID: string;
  REQUIRED_PREVIOUS_NODE: string;
}

export interface StopeRouteConfig {
  STOPE_ID: string;
  STOPE_NODE_ID: string;
  OVERRIDES: StopeRouteOverride[];
}

export interface ExcelExportData {
  nodes: ExcelNodeRow[];
  stopeRoutes: StopeRouteConfig[];
}

// Spool ids are opaque GUIDs, so an edge is found by its unordered endpoint pair.
const indexSpoolsByPair = (spools: Record<string, PCSpool>): Map<string, PCSpool> => reduce(
  (acc: Map<string, PCSpool>, spool: PCSpool) => acc.set(spool.pairKey(), spool),
  new Map<string, PCSpool>(),
  values(spools),
);

// The edge owns its pipe. The node pipe is only the fallback for a node with no resolved incoming edge.
const resolvePipeName = (
  node: PCNode,
  spool: PCSpool | undefined,
  pipes: Record<string, PCPipe>,
): string => {
  if (spool?.pipe) return spool.pipe.name;

  const foundPipe = values(pipes).find((p) => p.pipeId === node.pipeId);
  return foundPipe?.name || node.pipeId || 'Unknown';
};

const extractStopePipeInfo = (
  stopes: Record<string, PCStope[]>,
  pipes: Record<string, PCPipe>,
  nodeId: string,
): { stopePipeType?: string; horizontal?: number; vertical?: number } => {
  const stopeArray = stopes[nodeId];
  const stope = stopeArray && stopeArray.length > 0 ? stopeArray[0] : undefined;
  const stopePipeArray = stope?.data?.pipeArrays?.[0];

  if (!stopePipeArray?.pipeId) return {};

  const pipeId = (stopePipeArray.pipeId as any).value ?? stopePipeArray.pipeId;
  const foundPipe = values(pipes).find((p) => String(p.pipeId) === String(pipeId));
  const stopePipeType = foundPipe?.name || String(pipeId);

  return {
    stopePipeType,
    horizontal: stopePipeArray.pipeHorizontalLength,
    vertical: stopePipeArray.pipeVerticalLength,
  };
};

export const exportReticulationToExcel = (
  nodes: Record<string, PCNode>,
  stopes: Record<string, PCStope[]>,
  spools: Record<string, PCSpool>,
  pipes: Record<string, PCPipe>,
  plantNode: PCNode,
  biDirectionalBoreholes: boolean,
): ExcelExportData => {
  const { neighbors } = buildGraphIndexes(spools, biDirectionalBoreholes);
  const spoolsByPair = indexSpoolsByPair(spools);

  const nodesOnPaths = new Set<string>();
  const defaultPreviousNode = new Map<string, string>();
  const stopeRoutes: StopeRouteConfig[] = [];

  const allStopes: PCStope[] = [];
  forEach((stopeArray: PCStope[]) => {
    forEach((stope: PCStope) => allStopes.push(stope), stopeArray);
  }, values(stopes));

  const stopePathMap = new Map<string, Map<string, string>>();

  forEach((stope: PCStope) => {
    const result = dijkstraShortestPath(neighbors, stope.originNode.id, plantNode.id);
    const path = result?.path || [];
    path.reverse();

    const nodeToParent = new Map<string, string>();

    path.forEach((nodeId, index) => {
      nodesOnPaths.add(nodeId);

      if (index > 0) {
        const prevNodeId = path[index - 1];
        nodeToParent.set(nodeId, prevNodeId);

        if (!defaultPreviousNode.has(nodeId)) {
          defaultPreviousNode.set(nodeId, prevNodeId);
        }
      }
    });

    stopePathMap.set(stope.data.stopeId, nodeToParent);
  }, allStopes);

  forEach((stope: PCStope) => {
    const stopeNodeToParent = stopePathMap.get(stope.data.stopeId);
    if (!stopeNodeToParent) return;

    const overrides: StopeRouteOverride[] = [];

    stopeNodeToParent.forEach((requiredPrev, nodeId) => {
      const defaultPrev = defaultPreviousNode.get(nodeId);
      if (defaultPrev && defaultPrev !== requiredPrev) {
        overrides.push({
          NODE_ID: nodeId,
          REQUIRED_PREVIOUS_NODE: requiredPrev,
        });
      }
    });

    stopeRoutes.push({
      STOPE_ID: stope.data.stopeId,
      STOPE_NODE_ID: stope.originNode.id,
      OVERRIDES: overrides,
    });
  }, allStopes);

  const excelNodes: ExcelNodeRow[] = [];

  nodesOnPaths.forEach((nodeId) => {
    const node = nodes[nodeId];
    if (!node) return;

    const previousNodeId = defaultPreviousNode.get(nodeId);
    const spool = previousNodeId ? spoolsByPair.get(pairKey(previousNodeId, node.id)) : undefined;
    const pipeName = resolvePipeName(node, spool, pipes);
    const loopLength = spool?.chokeLength ?? 0;
    const { stopePipeType, horizontal, vertical } = extractStopePipeInfo(stopes, pipes, nodeId);
    const stopeArray = stopes[nodeId];
    const stope = stopeArray && stopeArray.length > 0 ? stopeArray[0] : undefined;

    excelNodes.push({
      NODE_ID: node.id,
      X: node.coords.x,
      Y: node.coords.y,
      Z: node.coords.z,
      PREVIOUS_NODE: previousNodeId || '',
      PIPE_NAME: pipeName,
      IS_STOPE: node.isStope || !!stope,
      IS_BACKFILL_PLANT: node.isPlantNode || node.id === plantNode.id,
      IS_PIT: node.isPit,
      IS_FRICTION_LOOP: loopLength > 0,
      FRICTION_LOOP_LENGTH: loopLength,
      PIT_NAME: node.pit,
      RUPTURE_DISK_PRESSURE: node.ruptureDiskPressure ?? 0,
      CHOKE_HEAD: node.chokeHead ?? 0,
      STOPE_PIPE_TYPE: stopePipeType,
      STOPE_PIPE_HORIZONTAL: horizontal,
      STOPE_PIPE_VERTICAL: vertical,
    });
  });

  return {
    nodes: pipe(sortBy(prop('NODE_ID')))(excelNodes) as ExcelNodeRow[],
    stopeRoutes: pipe(sortBy(prop('STOPE_ID')))(stopeRoutes) as StopeRouteConfig[],
  };
};
